// Copyright (c) 2021. Sendanor <info@sendanor.fi>. All rights reserved.

import * as React from "react";
import "./AgentListView.scss";
import FormAppClassName from "../../../../constants/FormAppClassName";
import Button from "../../../../nor/ui/components/button/Button";
import Icon from "../../../../nor/ui/components/icon/Icon";
import { AddIcon, EditIcon } from "../../../../assets/icon";
import { VoidCallback } from "../../../../nor/ts/interfaces/callbacks";
import AgentService, {
    AgentServiceDestructor,
    AgentServiceEvent
} from "../../../../services/AgentService";
import LogService from "../../../../nor/ts/LogService";
import RepositoryEntry from "../../../../nor/ts/simpleRepository/types/RepositoryEntry";
import { map } from "../../../../nor/ts/modules/lodash";
import Loader from "../../../../nor/ui/components/loader/Loader";
import { Link } from "react-router-dom";
import {
    getAppRoute_AGENT_VIEW
} from "../../../../constants/AppRoute";
import FormLoginModal from "../../../../modals/FormLoginModal";
import AgentPoolDTO from "../../../../nor/pipeline/dto/AgentPoolDTO";

const LOG = LogService.createLogger('AgentListView');

export interface AgentListViewProps {
    readonly className  ?: string;
    readonly hasSession  : boolean;
}

export interface AgentListViewState {

    readonly agentListLoaded : boolean;
    readonly agentList       : RepositoryEntry<AgentPoolDTO>[];

}

export class AgentListView extends React.Component<AgentListViewProps, AgentListViewState> {

    public static defaultProps: Partial<AgentListViewProps> = {};

    private readonly _addNewCallback  : VoidCallback;
    private readonly _createdCallback : VoidCallback;

    private _createdListener : AgentServiceDestructor | undefined;
    private _agentListLoading         : boolean;


    public constructor (props: AgentListViewProps) {

        super(props);

        this._agentListLoading = false;

        this.state = {
            agentListLoaded : false,
            agentList   : []
        };

        this._addNewCallback = this._addNew.bind(this);
        this._createdCallback = this._onCreated.bind(this);

    }

    public componentDidMount (): void {

        this._loadAgentList();

        this._createdListener = AgentService.on(AgentServiceEvent.POOL_CREATED, this._createdCallback);

        if ( !this.props.hasSession && !FormLoginModal.isOpen()) {
            FormLoginModal.openModal();
        }

    }

    public componentDidUpdate (prevProps: Readonly<AgentListViewProps>, prevState: Readonly<AgentListViewState>, snapshot?: any): void {

        if ( prevProps.hasSession !== this.props.hasSession && this.props.hasSession && !this._agentListLoading) {
            this._loadAgentList();
        }

    }

    public componentWillUnmount (): void {

        if (this._createdListener) {
            this._createdListener();
            this._createdListener = undefined;
        }

    }

    public render () {

        return (
            <div className={FormAppClassName.AGENT_LIST_VIEW + ' ' + (this.props.className ?? '')}>

                <header className={FormAppClassName.AGENT_LIST_VIEW + '-header'}>

                    <h1 className={FormAppClassName.AGENT_LIST_VIEW + '-header-title'}>Agents</h1>

                    <div className={FormAppClassName.AGENT_LIST_VIEW + '-header-buttons'}>

                    {this.props.hasSession ? (

                            <Button
                                click={this._addNewCallback}
                                className={FormAppClassName.AGENT_LIST_VIEW + '-header-add-button'}
                            ><Icon><AddIcon /></Icon> Create pool</Button>

                    ) : null}

                    </div>

                </header>

                <section className={FormAppClassName.AGENT_LIST_VIEW + '-content'}>

                    {this.props.hasSession ? (
                        <React.Fragment>

                            {this.state.agentListLoaded ? (
                                <React.Fragment>

                                    <ul className={FormAppClassName.AGENT_LIST_VIEW + '-list'}>{map(this.state.agentList, (item : RepositoryEntry<AgentPoolDTO>, index: number) => {
                                        const id = item.id;
                                        const name = item?.data?.model?.name;
                                        return (
                                            <li key={item.id} className={FormAppClassName.AGENT_LIST_VIEW + '-item'}>

                                                <span className={FormAppClassName.AGENT_LIST_VIEW + '-name'}>{name}</span>

                                                <Link to={getAppRoute_AGENT_VIEW(id)} className={FormAppClassName.AGENT_LIST_VIEW + '-button'}>
                                                    <Icon><EditIcon /></Icon>
                                                    <span className={FormAppClassName.AGENT_LIST_VIEW + '-button-text'}>Open</span>
                                                </Link>

                                            </li>
                                        );
                                    })}</ul>

                                    {this.state.agentList.length === 0 ? (
                                        <p>No agent pools.</p>
                                    ) : null}

                                </React.Fragment>

                                ) : (
                                <Loader />
                            )}

                        </React.Fragment>
                    ) : (
                        <p><strong>You need to login first.</strong></p>
                    )}

                </section>

            </div>
        );

    }


    private _onCreated () {

        this._loadAgentList();

    }

    private _addNew () {

        if (!this.props.hasSession) {
            LOG.error(`_onCreateForm: We did not have session!`)
            return
        }

        AgentService.createAgentPool({
            "name": `Pool ${ (this.state?.agentList?.length ?? 0) + 1 }`,
        });

    }

    private _loadAgentList () {

        if (!this.props.hasSession) {
            LOG.warn(`Warning! User has not logged in. Cannot load list.`);
            return;
        }

        if (this._agentListLoading) {
            LOG.warn(`Warning! Page already loading.`);
            return;
        }

        this._agentListLoading = true;

        AgentService.getAgentPoolList().then( (list) => {
            this._agentListLoading = false;
            LOG.debug(`Loaded agent list: `, list);
            this.setState({
                agentListLoaded: true,
                agentList: list
            });
        }).catch( (err) => {

            this._agentListLoading = false;

            LOG.error(`Error: `, err);

            // FIXME: Show error message

        });

    }

}

export default AgentListView;
